import { InputBase, Box, CircularProgress } from '@mui/material';
import { styled } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import searchIcon from '../assets/images/search-icon.svg';

const Search = styled('div')(({ theme }) => ({
  position: 'relative',
  borderRadius: 20,
  backgroundColor: '#ffffff',
  border: '1px solid #bdbdbd',
  height: 40,
  display: 'flex',
  alignItems: 'center',
  paddingLeft: theme.spacing(2),
  boxSizing: 'border-box',
  width: 'calc(100% - 32px)', // Account for margins
  margin: theme.spacing(0, 2),
  [theme.breakpoints.up('sm')]: {
    margin: theme.spacing(0, 2),
    width: 'auto', // Reset width for desktop
    flex: 1,
    minWidth: 0,
  },
}));

const SearchIconWrapper = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  marginRight: theme.spacing(1),
  '& img': {
    width: 20,
    height: 20,
  },
}));

const StyledInputBase = styled(InputBase)(({ theme }) => ({
  width: '100%',
  '& .MuiInputBase-input': {
    padding: theme.spacing(1),
    paddingLeft: theme.spacing(1),
    transition: theme.transitions.create('width'),
    fontWeight: 400,
    color: theme.palette.text.primary,
  },
}));

function SearchBar({ language, isMobile, selectedStore, onSelectedStoreChange }) {
  const { t } = useTranslation();
  const [searchTerm, setSearchTerm] = useState(selectedStore || '');
  const [stores, setStores] = useState([]);
  const [recentStores, setRecentStores] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // Debounced fetch function
  const debouncedFetchStores = useCallback(
    debounce(async (term) => {
      if (!term) {
        setStores([]);
        setShowDropdown(false);
        onSelectedStoreChange(null);
        setIsLoading(false);
        return;
      }

      try {
        const response = await axios.post(
          'https://searchstores-o5eqkgosgq-uc.a.run.app',
          { searchTerm: term }
        );
        setStores(response.data.stores || []);
        setShowDropdown(true);
      } catch (error) {
        console.error('Error fetching stores:', error);
      } finally {
        setIsLoading(false);
      }
    }, 300), // Wait 300ms after last keystroke before fetching
    []
  );

  // Update search term and trigger debounced fetch
  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    if (!value) {
      onSelectedStoreChange(null); // Clear selected store when search is cleared
    }
    setIsLoading(true);
    debouncedFetchStores(value);
  };

  // Clean up debounce on unmount
  useEffect(() => {
    return () => {
      debouncedFetchStores.cancel();
    };
  }, [debouncedFetchStores]);

  // Load recent stores from localStorage on component mount
  useEffect(() => {
    const storedRecentStores = JSON.parse(localStorage.getItem('recentStores')) || [];
    setRecentStores(storedRecentStores);
  }, []);

  const handleStoreClick = (store) => {
    // Update the recent stores list
    const updatedRecentStores = [store, ...recentStores.filter((s) => s.store_name_english !== store.store_name_english)].slice(0, 5);
    setRecentStores(updatedRecentStores);
    localStorage.setItem('recentStores', JSON.stringify(updatedRecentStores));

    // Pass the selected store to the parent
    onSelectedStoreChange(store.store_name_english);
    setSearchTerm(""); // Update the search term
    setShowDropdown(false); // Close the dropdown
  };

  const handleFocus = () => {
    if (!searchTerm && recentStores.length > 0) {
      setShowDropdown(true); // Show dropdown only if recent stores are not empty
    }
  };

  const handleBlur = () => {
    // Hide dropdown when the input loses focus
    setTimeout(() => setShowDropdown(false), 200);
  };

  const handleClearAll = () => {
    setRecentStores([]);
    localStorage.removeItem('recentStores');
    setShowDropdown(false); // Close the dropdown
  };

  return (
    <Box sx={{ position: 'relative', width: '100%' }}>
      <Search>
        <SearchIconWrapper>
          <img src={searchIcon} alt="Search" />
        </SearchIconWrapper>
        <StyledInputBase
          placeholder={t('labels.searchStores')}
          fullWidth
          inputProps={{
            style: {
              color: "theme.palette.text.primary", // Placeholder text color
              fontWeight: 500, // Ensure placeholder text is not bold
            },
          }}
          value={searchTerm}
          onChange={handleSearchChange}
          onFocus={handleFocus} // Trigger dropdown on focus
          onBlur={handleBlur}
        />
      </Search>

      {showDropdown && (searchTerm || recentStores.length > 0) && (
        <Box
          sx={{
            position: 'absolute',
            top: '100%', // Position the dropdown directly below the search bar
            left: isMobile ? '8px' : 15, // Add horizontal margin on mobile
            right: isMobile ? '8px' : 'auto', // Ensure the dropdown doesn't exceed the screen width on mobile
            width: isMobile ? 'calc(100% - 16px)' : '96.5%', // Adjust width to account for the horizontal margin on mobile
            zIndex: 10,
            mt: 1, // Add a small margin between the search bar and the dropdown
            maxHeight: 200,
            overflowY: 'auto',
            backgroundColor: '#fff',
            border: '1px solid #ddd',
            borderRadius: '4px',
            boxShadow: '0px 4px 6px rgba(0, 0, 0, 0.1)', // Optional: Add a shadow for better visibility
          }}
        >
          {isLoading ? (
            <Box 
              sx={{ 
                p: 2, 
                textAlign: 'center',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 1
              }}
            >
              <CircularProgress size={20} thickness={4} />
              {/* <span style={{ fontSize: '14px', color: '#666' }}>
                {t('labels.searching')}...
              </span> */}
            </Box>
          ) : (
            <>
              {!searchTerm && recentStores.length > 0 && (
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '8px 16px',
                    // borderBottom: '1px solid #ddd',
                    // backgroundColor: '#f9f9f9',
                  }}
                >
                  <span style={{ fontWeight: 500, fontSize: '14px', color: '#333' }}>
                    {t('labels.recent')}
                  </span>
                  <span
                    style={{
                      fontSize: '14px',
                      fontWeight: 500,
                      color: '#007bff',
                      cursor: 'pointer',
                    }}
                    onClick={handleClearAll}
                  >
                    {t('labels.clearAll')}
                  </span>
                </Box>
              )}

              {(searchTerm ? stores : recentStores).map((store) => (
                <Box
                  key={store.id}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    padding: '8px 16px',
                    cursor: 'pointer',
                    '&:hover': {
                      backgroundColor: '#f5f5f5',
                    },
                  }}
                  onClick={() => handleStoreClick(store)}
                >
                  <img
                    src={store.logo}
                    alt={store.store_name_english || store.store_name_arabic}
                    style={{
                      width: '40px',
                      height: '40px',
                      borderRadius: '50%',
                      marginRight: '16px',
                    }}
                  />
                  <span
                    style={{
                      fontSize: '16px',
                      fontWeight: '500',
                      color: 'black',
                      marginLeft: '8px',
                      marginRight: '8px',
                      textAlign: language === 'AR' ? 'right' : 'left',
                      flex: 1,
                    }}
                  >
                    {language === 'AR' ? store.store_name_arabic : store.store_name_english}
                  </span>
                </Box>
              ))}
            </>
          )}
        </Box>
      )}
    </Box>
  );
}

// Debounce utility function
function debounce(func, wait) {
  let timeout;
  const debouncedFn = function (...args) {
    const context = this;
    clearTimeout(timeout);
    timeout = setTimeout(() => func.apply(context, args), wait);
  };
  debouncedFn.cancel = function () {
    clearTimeout(timeout);
  };
  return debouncedFn;
}

export default SearchBar;